import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faCircleCheck,
  faCircleXmark,
  faFileImage,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";

import { getColorsElements, getMethodDisplay } from "./ProductDetailsLogo";
import { BaseLogo, isDigital } from "../../../../core/models/entities/BaseLogo";
import { LogoSize } from "../../../components/Logo/LogoSize";
import IconButton from "../../../components/LogoButton/IconButton";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface DesignPrintItemProps {
  logo: BaseLogo;
  addMoreAction?: () => void;
  placements?: string;
  colors: ReactNode;
  useTrueColors?: boolean;
  isDeleted?: boolean;
}
export const DesignPrintItem = ({
  logo,
  addMoreAction,
  colors,
  placements,
  useTrueColors,
  isDeleted,
}: DesignPrintItemProps) => {
  const { t } = useTranslation();
  const isRaster = isDigital(logo);
  const alt = t("orderCreate.logoAlt", { title: logo.title });
  const src =
    useTrueColors && logo.displayImage.trueColorsUrl
      ? logo.displayImage.trueColorsUrl
      : logo.displayImage.lowResUrl;

  const headerBorderClass = isDeleted ? "" : "border-x border-t";
  const bodyBorderClass = isDeleted ? "" : "border-y border-x";

  return (
    <div
      className={
        isDeleted
          ? "self-start flex flex-col bg-white border border-red-500 rounded shadow-[0_0_8px_rgba(239,68,68,0.55)]"
          : "flex flex-col bg-white"
      }
    >
      <div
        className={`flex flex-row items-center p-1.5 font-bold gap-2 text-[#1B76DF] bg-gray-100 ${headerBorderClass} rounded-t`}
      >
        {isDeleted && (
          <Tooltip
            title={t("orderCreate.deletedLogoTooltip")}
            placement="top"
            slotProps={{
              tooltip: { sx: { fontSize: "0.8rem", maxWidth: 320 } },
            }}
          >
            <FontAwesomeIcon
              className={"text-red-600 text-sm"}
              icon={faTriangleExclamation}
            />
          </Tooltip>
        )}
        <div className="border rounded-sm p-1 px-2 text-[#1B76DF] text-xs bg-[#FFFFFF]">
          <FontAwesomeIcon size={"1x"} icon={faFileImage} />
        </div>
        <div className="truncate text-[#1B76DF] text-xs font-bold">
          <a
            href={`/logo?searchTerm=${logo.product?.sku}&pageSize=25&pageIndex=1`}
            className={"hover:underline"}
          >
            {logo.title}
          </a>
        </div>
      </div>
      <div className={`flex flex-row bg-gray-100 ${bodyBorderClass} rounded-b`}>
        <div className="bg-white w-[120px] max-h-full flex items-center justify-center">
          <img
            className={
              isDeleted
                ? "w-[70px] h-[45px] object-contain bg-white opacity-50"
                : "w-[70px] h-[45px] object-contain bg-white rounded hover:border hover:p-2 origin-left hover:scale-[4] hover:ease-out transition-all duration-150 ease-out z-40"
            }
            src={src}
            alt={alt}
          />
        </div>
        <div
          className={`flex flex-col gap-1 text-xs p-2 ${isDeleted ? "opacity-60" : ""}`}
        >
          {logo.product?.sku && (
            <div className="flex flex-row gap-x-1 capitalize">
              <b>{t("item_no")}</b>
              <a
                href={`/logo?searchTerm=${logo.product?.sku}&pageSize=25&pageIndex=1`}
                className={"hover:underline"}
              >
                {logo.product?.sku}
              </a>
            </div>
          )}
          <div className="flex flex-row gap-x-1 capitalize">
            <b>{t("type")}</b>
            {logo.transferMethod.type.toLowerCase() === "direk tryk" ||
            logo.transferMethod.type.toLowerCase() === "screen print"
              ? t("orderCreate.screenPrint")
              : logo.transferMethod.type.toLowerCase() === "broderi" ||
                  logo.transferMethod.type.toLowerCase() === "embroidery"
                ? t("orderCreate.embroidery")
                : logo.transferMethod.type
                    .toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
          </div>
          {logo.transferMethod.type.toLowerCase() !== "direk tryk" &&
            logo.transferMethod.type.toLowerCase() !== "screen print" &&
            logo.transferMethod.type.toLowerCase() !== "broderi" &&
            logo.transferMethod.type.toLowerCase() !== "embroidery" && (
              <div className="flex flex-row gap-x-1 capitalize">
                <b>{t("orderCreate.method")}</b>
                {getMethodDisplay(logo)}
              </div>
            )}
          <div className="flex flex-row items-center gap-x-1">
            <b>{t("orderCreate.size")}</b>
            <LogoSize logo={logo} format="WWxYYmm" />
          </div>
          {placements && (
            <p>
              <b>{t("orderCreate.placement")}</b> {placements}
            </p>
          )}
          {addMoreAction && Math.max(0, logo?.inStorage ?? 0) > 0 && (
            <p>
              <b>{t("orderCreate.inStorage")}</b> {Math.max(0, logo?.inStorage ?? 0)}
            </p>
          )}
          <div className="flex flex-row gap-x-1 -mb-1">
            <span>
              <b>{t("orderCreate.colorsLabel")}</b>
            </span>
            <div>{colors}</div>
          </div>
          {(logo.transferMethod.type.toLowerCase() === "direk tryk" ||
            logo.transferMethod.type.toLowerCase() === "screen print" ||
            logo.transferMethod.type.toLowerCase() === "broderi" ||
            logo.transferMethod.type.toLowerCase() === "embroidery") && (
            <div className="h-3"></div>
          )}
          {addMoreAction && (
            <IconButton
              key={"add-order"}
              onClick={addMoreAction}
              icon={<FontAwesomeIcon icon={faCartPlus} />}
            >
              {t("orderCreate.addMore")}
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};
